<script setup>
/* global axios */
// Card da Meta Conversions API na página de Integrações — no MESMO padrão dos
// demais (logo + status + descrição + "Configurar"). O formulário abre num
// popup ao clicar em Configurar. Self-contained (axios direto).
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import { getCurrentCurrency } from 'dashboard/routes/dashboard/kanban/appointments/constants';

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/meta_capi_config`;
// default = moeda da conta (config única de moeda), ainda editável aqui
const defaultCurrency = () => getCurrentCurrency() || 'BRL';

const showModal = ref(false);
const saving = ref(false);
const checking = ref(false);
const statusResult = ref(null); // { connected, pixel_name, error }
const tokenSet = ref(false);
const enabled = ref(false);
const form = reactive({
  pixel_id: '',
  access_token: '',
  currency: defaultCurrency(),
  test_event_code: '',
  enabled: false,
});

const load = async () => {
  try {
    const { data } = await axios.get(base);
    const d = data.data || {};
    form.pixel_id = d.pixel_id || '';
    form.currency = d.currency || defaultCurrency();
    form.test_event_code = d.test_event_code || '';
    form.enabled = d.enabled || false;
    tokenSet.value = d.access_token_set || false;
    enabled.value = d.enabled || false;
  } catch (e) {
    /* silencioso */
  }
};

const openModal = () => {
  form.access_token = '';
  statusResult.value = null;
  showModal.value = true;
};

const checkStatus = async () => {
  checking.value = true;
  statusResult.value = null;
  try {
    const { data } = await axios.get(`${base}/status`);
    statusResult.value = data.data || { connected: false };
  } catch (e) {
    statusResult.value = {
      connected: false,
      error: e.response?.data?.error || t('META_CAPI.SAVE_ERROR'),
    };
  } finally {
    checking.value = false;
  }
};

const save = async () => {
  saving.value = true;
  const payload = {
    pixel_id: form.pixel_id,
    currency: form.currency,
    test_event_code: form.test_event_code,
    enabled: form.enabled,
  };
  if (form.access_token) payload.access_token = form.access_token;
  try {
    const { data } = await axios.put(base, { meta_capi_config: payload });
    const d = data.data || {};
    tokenSet.value = d.access_token_set || false;
    enabled.value = d.enabled || false;
    form.access_token = '';
    statusResult.value = null;
    showModal.value = false;
    useAlert(t('META_CAPI.SAVED'));
  } catch (e) {
    useAlert(e.response?.data?.error || t('META_CAPI.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div
    class="flex flex-col flex-1 p-4 m-px outline outline-n-container outline-1 bg-n-card rounded-xl"
  >
    <div class="flex items-start justify-between">
      <div
        class="flex items-center justify-center w-12 h-12 mb-2 border rounded-md shadow-sm border-n-weak bg-n-alpha-3"
      >
        <span class="i-lucide-target size-6 text-n-brand" />
      </div>
      <Label
        :label="
          enabled
            ? t('META_CAPI.STATUS.CONFIGURED')
            : t('META_CAPI.STATUS.PENDING')
        "
        :color="enabled ? 'teal' : 'slate'"
        compact
      />
    </div>
    <div class="flex flex-col flex-1 m-0">
      <div
        class="flex items-center justify-between mb-2 font-medium text-n-slate-12"
      >
        <span class="text-heading-3 text-n-slate-12">
          {{ t('META_CAPI.CARD_NAME') }}
        </span>
        <NextButton
          :label="t('META_CAPI.CONFIGURE')"
          icon="i-woot-settings"
          link
          xs
          @click="openModal"
        />
      </div>
      <p class="text-n-slate-11 text-body-main">
        {{ t('META_CAPI.DESCRIPTION') }}
      </p>
    </div>

    <woot-modal v-model:show="showModal" :on-close="() => (showModal = false)">
      <div class="flex flex-col h-auto overflow-auto w-[34rem] max-w-full">
        <woot-modal-header
          :header-title="t('META_CAPI.CARD_NAME')"
          :header-content="t('META_CAPI.DESCRIPTION')"
        />
        <form class="flex flex-col gap-1 px-8 py-3" @submit.prevent="save">
          <woot-input
            v-model="form.pixel_id"
            class="w-full"
            autocomplete="off"
            :label="t('META_CAPI.FORM.PIXEL_ID.LABEL')"
            :placeholder="t('META_CAPI.FORM.PIXEL_ID.PLACEHOLDER')"
          />
          <woot-input
            v-model="form.access_token"
            type="password"
            class="w-full"
            autocomplete="new-password"
            :label="t('META_CAPI.FORM.TOKEN.LABEL')"
            :placeholder="
              tokenSet
                ? t('META_CAPI.FORM.TOKEN.SET')
                : t('META_CAPI.FORM.TOKEN.PLACEHOLDER')
            "
            :help-text="tokenSet ? t('META_CAPI.FORM.TOKEN.KEEP') : ''"
          />
          <div class="grid grid-cols-2 gap-3">
            <woot-input
              v-model="form.currency"
              class="w-full"
              autocomplete="off"
              :label="t('META_CAPI.FORM.CURRENCY.LABEL')"
              :placeholder="defaultCurrency()"
            />
            <woot-input
              v-model="form.test_event_code"
              class="w-full"
              autocomplete="off"
              :label="t('META_CAPI.FORM.TEST_CODE.LABEL')"
              :placeholder="t('META_CAPI.FORM.TEST_CODE.PLACEHOLDER')"
              :help-text="t('META_CAPI.FORM.TEST_CODE.HELP')"
            />
          </div>
          <label class="flex items-center gap-2 py-2 text-sm text-n-slate-12">
            <input
              v-model="form.enabled"
              type="checkbox"
              class="accent-n-brand"
            />
            {{ t('META_CAPI.FORM.ENABLED.LABEL') }}
          </label>
          <!-- resultado do "Verificar status" -->
          <div
            v-if="statusResult"
            class="flex items-start gap-2 p-2 mt-1 text-xs rounded-lg"
            :class="
              statusResult.connected
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-ruby-2 text-n-ruby-11'
            "
          >
            <span
              class="mt-0.5 size-3.5 shrink-0"
              :class="
                statusResult.connected
                  ? 'i-lucide-badge-check'
                  : 'i-lucide-badge-alert'
              "
            />
            <span v-if="statusResult.connected">
              {{ t('META_CAPI.CHECK.CONNECTED') }}
              <template v-if="statusResult.pixel_name">
                · {{ statusResult.pixel_name }}
              </template>
            </span>
            <span v-else>
              {{ t('META_CAPI.CHECK.DISCONNECTED') }}: {{ statusResult.error }}
            </span>
          </div>

          <div class="flex items-center justify-between w-full gap-2 py-2">
            <NextButton
              faded
              slate
              icon="i-lucide-plug-zap"
              :label="t('META_CAPI.CHECK.BUTTON')"
              :is-loading="checking"
              @click.prevent="checkStatus"
            />
            <div class="flex items-center gap-2">
              <NextButton
                faded
                slate
                type="reset"
                :label="t('META_CAPI.FORM.CANCEL')"
                @click.prevent="showModal = false"
              />
              <NextButton
                type="submit"
                :label="t('META_CAPI.FORM.SAVE')"
                :is-loading="saving"
              />
            </div>
          </div>
        </form>
      </div>
    </woot-modal>
  </div>
</template>
